import { useIsMutating } from '@tanstack/react-query'
import React, { useCallback, useId } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import SvgAttachmentFile from 'src/components/Svgs/SvgAttachmentFile'
import { useTranslate } from 'src/localization/Trns'
import { receiptAttachmentsMutationKeys } from 'src/queries/receiptAttachments/receiptAttachments.queries'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'

import wideDropzoneStyle from './WideDropzone.style'

interface WideDropzoneProps {
  uploadAttachments: (attachments: PostReceiptAttachmentRequest[]) => void
  setAttachmentError?: (
    errorMsg: string | React.ReactElement | undefined,
  ) => void
  setErrors?: (onlyOneReceipt: boolean) => void
  attachmentLimit?: number
  attachmentsLength?: number
  isMissing?: boolean
}

export default function WideDropzone({
  uploadAttachments,
  setAttachmentError,
  setErrors,
  attachmentLimit,
  attachmentsLength,
  isMissing,
}: WideDropzoneProps): React.ReactElement {
  const translate = useTranslate()
  const isPushingToERP =
    useIsMutating({
      mutationKey: receiptAttachmentsMutationKeys.pushToERP(),
    }) > 0

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]): void => {
      setAttachmentError?.(undefined)

      if (
        attachmentLimit !== undefined &&
        attachmentsLength !== undefined &&
        attachmentsLength + acceptedFiles.length + fileRejections.length >
          attachmentLimit
      ) {
        setAttachmentError?.(
          translate('Only ${numImages} images can be uploaded at a time', {
            numImages: attachmentLimit.toString(),
          }),
        )
        return
      }

      const attachments = acceptedFiles.map((file) => {
        const form = new FormData()
        form.append('file', file, file.name)
        const thumbnailUrl = URL.createObjectURL(file)
        return {
          formData: form,
          uploadId: thumbnailUrl,
          thumbnailUrl,
          fileName: file.name,
        }
      })
      uploadAttachments(attachments)
      if (fileRejections.length > 0) {
        setAttachmentError?.(translate('Files cannot exceed 10MB'))
      }
      setErrors?.(false)
    },
    [
      uploadAttachments,
      attachmentLimit,
      attachmentsLength,
      setAttachmentError,
      setErrors,
      translate,
    ],
  )

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/tiff': ['.tiff', '.tif'],
      'image/gif': ['.gif'],
      'image/bmp': ['.bmp'],
      'image/heic': ['.heic'],
      'image/svg+xml': ['.svg'],
    },
    maxSize: 10000000,
  })

  const classNames = [
    'WideDropzone',
    isPushingToERP ? 'disabled' : '',
    isMissing ? 'isMissing' : '',
  ]
    .filter(Boolean)
    .join(' ')

  const missingLabelId = useId()

  return (
    <div
      css={wideDropzoneStyle}
      className={classNames}
      data-testid="add-attachment"
      aria-invalid={isMissing || undefined}
      aria-describedby={isMissing ? missingLabelId : undefined}
      {...getRootProps()}
    >
      <input className="dropzone-input" {...getInputProps()} />
      <SvgAttachmentFile />
      <span className="primary">
        <span className="link">{translate('Click to upload')}</span>{' '}
        {translate('or drag and drop')}
      </span>
      {isMissing ? (
        <span id={missingLabelId} className="missing-label">
          {translate('Missing receipt')}
        </span>
      ) : (
        <span className="secondary">
          {translate('PDF, SVG, PNG, JPG or GIF (max. 10MB)')}
        </span>
      )}
    </div>
  )
}
